// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Stack } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { AppointmentsSection } from '../components/dashboard/AppointmentsSection';
import { CareTeamNotesSection } from '../components/dashboard/CareTeamNotesSection';
import { DashboardAlerts } from '../components/dashboard/DashboardAlerts';
import { HistoryOfChargesSection } from '../components/dashboard/HistoryOfChargesSection';
import { IntakeQuestionnaireBanner } from '../components/dashboard/IntakeQuestionnaireBanner';
import { SleepTestsSection } from '../components/dashboard/SleepTestsSection';
import { TreatmentPlanSection } from '../components/dashboard/TreatmentPlanSection';
import { WelcomeHero } from '../components/dashboard/WelcomeHero';
import { PaymentBanner } from '../components/PaymentBanner';
import { useAppointmentDetails } from '../hooks/useAppointmentDetails';
import { useCareTeamNotes } from '../hooks/useCareTeamNotes';
import { useMyChartData } from '../hooks/useMyChartData';
import { usePatientAppointments } from '../hooks/usePatientAppointments';
import { usePatientCharges } from '../hooks/usePatientCharges';
import { useTreatmentPlan } from '../hooks/useTreatmentPlan';
import { useUnreadCareTeamNotes } from '../hooks/useUnreadCareTeamNotes';
import classes from './PatientDashboardPage.module.css';

export function PatientDashboardPage(): JSX.Element {
  const profile = useMedplumProfile() as Patient | undefined;

  const {
    upcoming: upcomingAppointments,
    loading: appointmentsLoading,
    refresh: refreshAppointments,
  } = usePatientAppointments();
  const { details: appointmentDetails, loading: detailsLoading } = useAppointmentDetails(upcomingAppointments);

  const { notes, loading: notesLoading } = useCareTeamNotes();
  const { unreadCount, markAllAsSeen } = useUnreadCareTeamNotes(notes, profile?.id);

  const { description: planDescription, loading: planLoading } = useTreatmentPlan();
  const { charges, overdueCount, loading: chargesLoading } = usePatientCharges();

  const { hsatOrders, hsatReports, loading: chartLoading } = useMyChartData();
  return (
    <Stack gap="md" p="md" className={classes.root}>
      <DashboardAlerts unreadNotesCount={unreadCount} overduePaymentsCount={overdueCount} />
      <PaymentBanner charges={charges} />

      <WelcomeHero patient={profile} />

      <IntakeQuestionnaireBanner />

      <AppointmentsSection
        appointments={appointmentDetails}
        loading={appointmentsLoading || detailsLoading}
        onRefresh={refreshAppointments}
      />

      <CareTeamNotesSection notes={notes} loading={notesLoading} onModalOpen={markAllAsSeen} />

      <SleepTestsSection hsatOrders={hsatOrders} hsatReports={hsatReports} loading={chartLoading} />

      <TreatmentPlanSection description={planDescription} loading={planLoading} />

      <HistoryOfChargesSection charges={charges} loading={chargesLoading} />
    </Stack>
  );
}
